import { t } from '@lingui/core/macro';
import { UWEEntity } from '../../../api/types';
import { FileStage, InventoryScheduleDef, InventoryScheduleSubmissionStatus, UploadStatus } from './types';

/**
 * An Amendment carries the amended report (expanded) under data.report and edits a snapshot of
 * it under data.amendedData; it has none of the report's own metadata.
 */
export function isAmendmentEntity(entity: UWEEntity | undefined): boolean {
  return !!entity?.data?.report && !!entity?.data?.amendedData;
}

/** The report's metadata — off the entity itself, or off the amended report for an amendment. */
export function getReportMetadata(entity: UWEEntity | undefined): Record<string, any> | undefined {
  return isAmendmentEntity(entity) ? entity?.data?.report?.metadata : entity?.metadata;
}

/**
 * The object holding the inventorySchedules / inventoryNothingToReport maps: the report's data,
 * or the amendment's amendedData snapshot (where the backend writes an amendment's changes).
 */
export function getInventoryData(entity: UWEEntity | undefined): Record<string, any> | undefined {
  return isAmendmentEntity(entity) ? entity?.data?.amendedData : entity?.data;
}

export const STATUS_COLOR: Record<UploadStatus, string> = {
  'Not Uploaded': '#7a5800',
  Uploaded: '#1a5f6e',
  Partial: '#8e44ad',
  Error: '#c0392b',
  Done: '#5a8a6a',
  'Nothing to Report': '#6c757d',
};

export const STATUS_ICON: Record<UploadStatus, string> = {
  Done: 'Check',
  'Not Uploaded': 'phosphor-X',
  Error: 'Warning',
  Partial: 'FileDashed',
  Uploaded: 'FileArrowUp',
  'Nothing to Report': 'MinusCircle',
};

/** Who drives a step of the inventory upload process: the filer offline, this page, or the final submission. */
export type ProcessPhase = 'prepare' | 'upload' | 'submit';

export interface ProcessStep {
  label: string;
  icon: string;
  phase: ProcessPhase;
}

export function getProcessSteps(): ProcessStep[] {
  return [
    { label: t`1. Download template`, icon: 'Download', phase: 'prepare' },
    { label: t`2. Fill out document`, icon: 'NotePencil', phase: 'prepare' },
    { label: t`3. Upload file`, icon: 'Upload', phase: 'upload' },
    { label: t`4. Format validation`, icon: 'ShieldCheck', phase: 'upload' },
    { label: t`5. File stored`, icon: 'Database', phase: 'upload' },
    { label: t`6. Submit for review`, icon: 'PaperPlaneRight', phase: 'submit' },
  ];
}

export const INITIAL_FILE_STAGE: FileStage = { file: null, validationState: 'idle' };

/** Key of the report form's tab that hosts OcifInventoryScheduleUploadPanel. */
export const INVENTORY_SCHEDULES_TAB = 'inventorySchedules';

const SUBMISSION_TO_UPLOAD_STATUS: Record<InventoryScheduleSubmissionStatus, UploadStatus> = {
  missing: 'Not Uploaded',
  uploaded: 'Uploaded',
  'nothing to report': 'Nothing to Report',
};

export function inventoryUploadStatus(status: InventoryScheduleSubmissionStatus | undefined): UploadStatus {
  return SUBMISSION_TO_UPLOAD_STATUS[status ?? 'missing'] ?? 'Not Uploaded';
}

/** Entity metadata stores string lists either as JSON text or as an already-parsed array. */
export function parseNameList(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const INVENTORY_SCHEDULES: InventoryScheduleDef[] = [];

const INVENTORY_SCHEDULE_SUBMISSION_STATUSES: InventoryScheduleSubmissionStatus[] = ['missing', 'uploaded', 'nothing to report'];

function isInventorySchedulesShape(value: unknown): value is Record<string, InventoryScheduleSubmissionStatus> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const values = Object.values(value as Record<string, unknown>);
  return values.length > 0 && values.every(v => INVENTORY_SCHEDULE_SUBMISSION_STATUSES.includes(v as InventoryScheduleSubmissionStatus));
}

/**
 * OcifInventoryScheduleUploadPanel writes its per-schedule statuses to a key named
 * "inventorySchedules" somewhere in the overall form tree (its own field path, not
 * necessarily nested under this component's formData) — so this walks the whole tree
 * looking for it rather than reading a known path.
 */
export function findInventorySchedulesInFormData(node: unknown, depth = 0): Record<string, InventoryScheduleSubmissionStatus> | null {
  if (depth > 12 || !node || typeof node !== 'object') return null;
  if (!Array.isArray(node)) {
    const candidate = (node as Record<string, unknown>).inventorySchedules;
    if (isInventorySchedulesShape(candidate)) return candidate;
  }
  const children = Array.isArray(node) ? node : Object.values(node as Record<string, unknown>);
  for (const child of children) {
    const found = findInventorySchedulesInFormData(child, depth + 1);
    if (found) return found;
  }
  return null;
}

export function hexToRgb(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export function buildDropZoneCss(primaryMain: string): string {
  const rgb = hexToRgb(primaryMain);
  return `
    .bulk-drop-zone {
      border-radius: 5px;
      cursor: pointer;
      background-color: rgba(${rgb}, 0.04);
      border: 1px dashed ${primaryMain};
      transition: background-color 0.15s ease, box-shadow 0.15s ease;
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
    .bulk-drop-zone:hover {
      background-color: rgba(${rgb}, 0.09);
      box-shadow: 0 0 0 3px rgba(${rgb}, 0.18);
    }
    .bulk-drop-zone.is-dragging {
      background-color: rgba(${rgb}, 0.14);
      box-shadow: 0 0 0 4px rgba(${rgb}, 0.28);
    }
    .bulk-file-loaded {
      background-color: rgba(${rgb}, 0.05) !important;
      border-color: rgba(${rgb}, 0.35) !important;
      color: inherit !important;
    }
  `;
}
